/**
 * Audit screen — the old app's "Home Screen": search evaluated assessments,
 * preview the report PDF inline, download it. Restricted to administrators,
 * auditors and recruitment by the route guard and the API's audit view.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { WritingBadge } from "../components/StatusBadge";
import { fmtDate, fmtScore } from "../lib/format";
import type { AssessmentSummary } from "../api/types";

export default function AuditPage() {
  const [q, setQ] = useState("");
  const [home, setHome] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState<AssessmentSummary | null>(null);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");

  const { data: lookups } = useQuery({ queryKey: ["lookups"], queryFn: api.lookups });
  const { data: rows, isLoading, error, refetch } = useQuery({
    queryKey: ["audit", q, home, from, to],
    queryFn: () => api.assessments.list({ q, home, from, to, status: "evaluated", view: "audit" }),
  });

  const pick = async (r: AssessmentSummary) => {
    setSelected(r);
    setReportError("");
    if (reportUrl) URL.revokeObjectURL(reportUrl);
    setReportUrl(null);
    // evaluated records always have a report: the API serves the SharePoint
    // copy when one exists and regenerates the PDF on demand otherwise
    setLoadingReport(true);
    try {
      setReportUrl(await api.assessments.reportUrl(r.id));
    } catch (err) {
      setReportError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-liaise-navy">Audit</h1>
        <p className="text-sm text-slate-500">Evaluated assessments with their completed reports.</p>
      </div>

      <div className="card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="label" htmlFor="audit-search">Search</label>
          <input id="audit-search" className="input" placeholder="Name, email or Test ID" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="audit-home">Home</label>
          <select id="audit-home" className="input" value={home} onChange={(e) => setHome(e.target.value)}>
            <option value="">All</option>
            {(lookups?.home ?? []).map((h) => <option key={h.id} value={h.value}>{h.value}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="audit-from">From</label>
          <input id="audit-from" type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="audit-to">To</label>
          <input id="audit-to" type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4 items-start">
        <div className="lg:col-span-2 card overflow-hidden max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <p className="px-5 py-6 text-sm text-slate-400">Loading...</p>
          ) : error ? (
            <div className="px-5 py-6 space-y-3">
              <p role="alert" className="text-sm text-liaise-red">Couldn't load the audit reports. Please try again.</p>
              <button className="btn-secondary" onClick={() => refetch()}>Try again</button>
            </div>
          ) : (rows ?? []).length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400">No evaluated assessments match.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows!.map((r) => (
                <button
                  key={r.id}
                  onClick={() => pick(r)}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-liaise-navy-50/60 ${selected?.id === r.id ? "bg-liaise-navy-50" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-liaise-navy">{r.first_name} {r.last_name}</span>
                    <WritingBadge result={r.writing_result} />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.email ?? "-"} · {r.home ?? "-"} · {fmtDate(r.assessment_date)}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    Eng {fmtScore(r.english_total, r.english_max)} · Maths {fmtScore(r.maths_total, r.maths_max)} · {r.position ?? "-"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3 card overflow-hidden min-h-[70vh] flex flex-col">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-8 text-center">
              Select an assessment to preview its report.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 gap-2 flex-wrap">
                <span className="font-semibold text-liaise-navy text-sm">
                  {selected.first_name} {selected.last_name}
                  <span className="font-mono text-xs text-slate-400 ml-2">{selected.test_id}</span>
                </span>
                <div className="flex gap-2">
                  <Link className="btn-secondary text-xs" to={`/assessment/${selected.id}`}>Full record</Link>
                  {reportUrl && (
                    <a className="btn-primary text-xs" href={reportUrl} download={selected.report_file ?? "report.pdf"}>
                      Download
                    </a>
                  )}
                </div>
              </div>
              <div className="flex-1">
                {loadingReport ? (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">Loading report...</div>
                ) : reportError ? (
                  <div className="h-full flex items-center justify-center text-sm text-liaise-red p-6 text-center">{reportError}</div>
                ) : reportUrl ? (
                  <iframe src={reportUrl} title="Report" className="w-full h-full min-h-[60vh]" />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
